<script lang="ts" setup>
import { computed, onMounted, watch, useTemplateRef, onBeforeUnmount } from 'vue';
import { createChart, CandlestickSeries, BaselineSeries } from 'lightweight-charts';
import type { IChartApi, ISeriesApi, UTCTimestamp } from 'lightweight-charts';
import { useThemeStore } from '@/stores/themeStore';
import { buildDefaultChartOptions, noExponents } from '@/utils';
import { useTzDate } from '@/composable/useTzDate';

const chartContainer = useTemplateRef('chartContainer');
let candleSeries = null as ISeriesApi<'Candlestick'> | null
let lineSeries = null as ISeriesApi<'Baseline'> | null
let chart = null as IChartApi | null
const themeStore = useThemeStore()
const { offsetMs } = useTzDate()

const emit = defineEmits(["mounted"])
type OhlcvFormat = Array<[number, number, number, number, number]>
type LineFormat = Array<[number, number]>

type ChartType = 'ohlcv' | 'line'

const props = defineProps<{
  height: string
  type: ChartType
  yFormat?: '1s' | null
  minMove: number
  data?: Array<Array<number>> | null
}>()

const precision = computed(() => noExponents(props.minMove ?? 0)?.split('.')[1]?.length)

const reset = () => {
  chart?.timeScale().fitContent()
}

defineExpose({ reset })
const updateData = (v: OhlcvFormat | LineFormat) => {
  if (props.type == 'ohlcv') {
    const fmt = v.map(
      ([ts, o, h, l, c]) => ({
        time: Number(ts) as UTCTimestamp,
        open: Number(o),
        high: Number(h),
        low: Number(l),
        close: Number(c),
      })
    )
    if (candleSeries) {
      const oldData = candleSeries?.data()
      if (oldData.length > 0) {
        // const updatesOnly = extractNewCandles(oldData as OhlcData<UTCTimestamp>[], fmt as OhlcData<UTCTimestamp>[])
        const updatesOnly = fmt.slice(-1)
        if (updatesOnly.length > 0) {
          updatesOnly.forEach((x) => {
            candleSeries?.update(x)
          })
        }
      } else {
        candleSeries?.setData(fmt)
      }
    }
  } else if (props.type == 'line') {
    const fmt = v.map(
      ([ts, v]) => ({
        time: ts as UTCTimestamp,
        value: v,
      })
    )
    lineSeries?.setData(fmt)
  }
}

const buildSeriesOptions = () => {
  return {
    priceFormat: {
      precision: precision.value ?? 5,
      minMove: props.minMove ?? 0.00001,
    },
  };
};

const initChart = () => {
  if (!chartContainer.value) {
    return
  }
  chart = createChart(chartContainer.value, buildDefaultChartOptions(props.yFormat, offsetMs));
  candleSeries = chart.addSeries(CandlestickSeries, buildSeriesOptions())
  lineSeries = chart.addSeries(BaselineSeries)
  updateData(props.data as OhlcvFormat | LineFormat)
}

watch(() => props.minMove, () => {
  candleSeries?.applyOptions(buildSeriesOptions())
})

onMounted(async () => {
  emit("mounted")
  initChart()
})

onBeforeUnmount(() => {
  chart?.remove();
})

watch(
  () => props.data,
  (v) => {
    updateData(v as OhlcvFormat | LineFormat)
  },
)

watch(
  () => themeStore.isDark,
  () => {
    chart?.applyOptions(buildDefaultChartOptions(props.yFormat, offsetMs))
  }
)
</script>

<template>
  <div ref="chartContainer" :style="`height: ${props.height}`"></div>
</template>

<style>
#tv-attr-logo {
  display: none;
}
</style>
